<template>
  <div class="b-collapse">
    <div
      ref="wrapper"
      class="b-collapse__wrapper"
      :class="{
        'has-scroll': hasScroll && !disabled,
        'is-active': isActive && !disabled,
        'is-disabled': disabled,
      }"
      :style="wrapperStyle"
    >
      <div ref="content" class="b-collapse__content">
        <slot>
          <template v-for="(item, index) in list">
            <span
              :key="index"
              class="b-collapse__selector-by-count"
              v-text="item"
            />
          </template>
        </slot>
      </div>
    </div>

    <v-btn
      v-show="!disabled && hasBtn"
      dense
      depressed
      x-small
      outlined
      :class="btnClass"
      :ripple="false"
      class="b-collapse__button"
      @click="isActive = !isActive"
      v-text="isActive ? hideTitle : showTitle"
    />
  </div>
</template>

<script>
import throttle from 'lodash/throttle'
import chunk from 'lodash/chunk'
import max from 'lodash/max'
import sum from 'lodash/sum'


const THROTTLE_TIME = 150

const getNodeHeightList = (nodes) => [...nodes].map((node) => {
  const { height } = node?.getBoundingClientRect()
  const styles = window.getComputedStyle(node)
  let { marginTop, marginBottom } = styles

  marginTop = parseInt(marginTop || 0, 10)
  marginBottom = parseInt(marginBottom || 0, 10)
  return height + marginTop + marginBottom
})

const getCountPerLine = (heightList, contentHeight) => {
  let countPerLine = 1
  let height = sum(heightList)

  while (contentHeight < height - 2 && countPerLine <= heightList.length) {
    countPerLine += 1
    height = sum(chunk(heightList, countPerLine).map(max))
  }

  return countPerLine
}

export default {
  name: 'BCollapse',
  inheritAttrs: false,
  props: {
    value: Boolean,
    disabled: Boolean,
    list: {
      type: Array,
    },
    minHeight: {
      type: [Number, String],
      default: void 0,
    },
    maxHeight: {
      type: [Number, String],
    },
    showTitle: {
      type: String,
      default: 'Показать',
    },
    hideTitle: {
      type: String,
      default: 'Свернуть',
    },
    selectorByCount: {
      type: String,
      default: '.b-collapse__selector-by-count',
    },
    minHeightByCount: Number,
    maxHeightByCount: Number,
    btnClass: [Array, Object, String],
  },
  data() {
    return {
      wrapperHeight: null,
      contentHeight: null,
      calculatedMinHeight: 0,
      calculatedMaxHeight: 0,
      isActive: false,
    }
  },
  computed: {
    hasBtn() {
      return this.contentHeight > this.calculatedMinHeight
    },
    hasScroll() {
      return (
        this.contentHeight > this.wrapperHeight
        && this.isActive
      )
    },
    wrapperStyle() {
      if (this.disabled) {
        return ''
      }

      const height = this.wrapperHeight || this.minHeight
      return { height: height && `${height}px` }
    },
  },
  watch: {
    list() {
      this.$nextTick(this.init)
    },
    value(value) {
      this.isActive = value
    },
    isActive: 'onChangeActive',
  },
  mounted() {
    this.init = throttle(this.init, THROTTLE_TIME)
    window.addEventListener('resize', this.init)
    this.$nextTick(this.init)
    this.$nextTick(this.onChangeActive)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.init)
  },
  methods: {
    init() {
      this.setContentHeight()
      this.setCalculatedHeight()
      this.onChangeActive()
    },
    setContentHeight() {
      const rect = this.$refs.content?.getBoundingClientRect()
      this.contentHeight = rect?.height
    },
    onChangeActive() {
      if (this.isActive) {
        this.showContent()
      } else {
        this.$refs.wrapper?.scrollTo(0, 0)
        this.wrapperHeight = this.calculatedMinHeight
      }

      this.$emit('input', this.isActive)
    },
    showContent() {
      const heigth = this.calculatedMaxHeight

      if (heigth && this.contentHeight > heigth) {
        this.wrapperHeight = heigth
      } else {
        this.wrapperHeight = this.contentHeight
      }
    },
    setCalculatedHeight() {
      const calculateByCont = (
        this.selectorByCount
        && (this.minHeightByCount || this.maxHeightByCount)
      )

      if (calculateByCont) {
        this.calculateHeightByCount()
      } else {
        this.calculatedMinHeight = this.minHeight
        this.calculatedMaxHeight = this.maxHeight
      }
    },
    calculateHeightByCount() {
      const children = this.$el.querySelectorAll(this.selectorByCount)

      if (children.length === 0) {
        // eslint-disable-next-line no-console
        console.log(`[App warn]: селектор "${this.selectorByCount}" не нашел ни одного элемента`)
      }

      const heightList = getNodeHeightList(children)
      const countPerLine = getCountPerLine(heightList, this.contentHeight)
      const heightListPerLine = chunk(heightList, countPerLine).map(max)

      let sumHeight = 0
      const firstByCount = this.minHeightByCount - 1
      const lastByCount = (this.maxHeightByCount || children.length) - 1

      heightListPerLine.forEach((height, index) => {
        sumHeight += height

        if (index === firstByCount) {
          this.calculatedMinHeight = sumHeight
        }

        if (index === lastByCount) {
          this.calculatedMaxHeight = sumHeight
        }
      })
    },
  },
}
</script>

<style lang="scss">
.b-collapse {
  &__wrapper {
    margin-bottom: 6px;
    overflow: hidden;
    transition: height 0.3s;

    &.has-scroll {
      overflow-y: scroll;
    }

    &.is-active {
      height: 200px;
    }
  }

  &__content {
    display: flex;
    flex-wrap: wrap;
  }

  &__selector-by-count {
    margin-left: 8px;

    &::after {
      display: inline;
      content: ", ";
    }

    &:last-child::after {
      display: none;
    }
  }
}
</style>
